import React, { useEffect, useState } from 'react'
import { RouterProvider, Outlet, Navigate } from 'react-router'
import { createBrowserRouter } from 'react-router-dom'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import './i18n'
import Communities from './components/Communities/Communities'
import AddCommunity from './components/Communities/AddCommunity'
import AddProgram from './components/Programs/AddProgram'
import { muiTheme } from './stylesheet'
import CssBaseline from '@mui/material/CssBaseline'
import AuthProvider, { useAuth } from './components/Routes/AuthProvider'
import PrivateRoute from './components/Routes/PrivateRoute'
import AppScaffold from './AppScaffold'
import { TitleProvider } from './TitleContext'
import { UserProvider } from './UserContext'
import CommunityDetails, {
  CommunityProgramLayout,
} from './components/Communities/CommunityDetails'
import ProgramDetails from './components/Programs/ProgramDetails'
import Events from './components/Events/Events'
import EventDetails from './components/Events/EventDetails'
import AddEvent from './components/Events/AddEvent'
import Account, {
  AccountSettingsTabLayout,
  BillingTabLayout,
  EnrollmentCardLayout,
  ProfileTabLayout,
} from './components/Account/Account'
import ScrollToTop from './components/Routes/ScrollToTop'
import Business from './components/Business/Business'
import BusinessContent from './components/Business/BusinessContent'
import LearningPath from './components/Business/LearningPath'
import LearningCenter from './components/LearningCenter/LearningCenter'
import ContentNDA from './components/Business/ContentNDA'
import TermsOfUse from './components/TermsOfUse/TermsOfUse'
import { SnackbarProvider as NotiSnackbarProvider } from 'notistack'
import { LoadingProvider } from './components/Context/LoadingContext'
import EmptyFamilies from './components/Families/EmptyFamilies'
import FamilyProfile from './components/Families/FamilyProfile'
import InviteFamily from './components/Families/InviteFamily'
import InvoiceDetails from './components/Families/InvoiceDetails'
import SnackbarProvider from './components/Context/SnackbarContext'
import NotistackSnackbarKeyProvider from './components/Context/NotistackSnackbarKeyProvider'
import NavigationAwayProvider from './components/Context/NavigationAwayContext'
import Login from './components/Login/Login'
import ResetPassword from './components/Login/ResetPassword'
import TeamTab from './components/Team/TeamTab'
import AddTeamMember from './components/Team/AddTeamMember'
import UserAccounts from './components/Admin/UserAccounts/UserAccounts'
import RoleDetails from './components/Admin/Roles/RoleDetails'
import AddRole from './components/Admin/Roles/AddRole'
import AddRegion from './components/Admin/Regions/AddRegion'
import Regions from './components/Admin/Regions/Regions'
import LearningCenterProvider from './components/Context/LearningCenterContext'
import RegionDetails from './components/Admin/Regions/RegionDetails'
import CountryInformation from './components/Card/CountryInformation'
import CommunitiesProvider from './components/Context/CommunitiesContext'
import ErrorElement from './components/Elements/ErrorElement'
import {
  CommunityFamilyLayout,
  CommunityDetailsLayout,
} from './components/Communities/CommunityDetails'
import LearningCenterContent from './components/LearningCenter/LearningCenterContent'
import { ParentIndex } from './components/Events/ParentIndex'
import InviteForm from './components/Login/InviteForm'
import InviteProvider from './components/Context/InviteContext'
import DirectorDashboardDetails from './components/Card/DirectorDashboardDetails'
import DashboardTab from './components/Account/Dashboard/DashboardTab'
import { MaterialDesignContent } from 'notistack'
import { styled } from '@mui/system'
import AccountProvider from './components/Context/AccountContext'
import CountryProvider from './components/Context/CountryContext'
import InvitationSummaryCard from './components/Card/InvitationSummaryCard'
import InvitesLayout from './components/Account/Invites/InvitesLayout'
import PendingInvitationsCard from './components/Card/PendingInvitationsCard'
import AssignChildrenCard from './components/Card/AssignChildrenCard'
import AddChildrenCard from './components/Card/AddChildrenCard'
import ProgramDetailsProvider from './components/Context/ProgramDetailsContext'
import CookieBanner from './components/CookieBanner'
import { ScrollContextProvider } from './components/Context/ScrollContext'
import FullScreenContainer from './components/LearningCenter/FullScreenContainer'
import { LearningCenterTabs } from './utils/searchAndFilterEnums'
import ContextResetter from './components/Context/ContextResetter'
import BusinessProvider from './components/Context/BusinessContext'
import UpdateParentInfoCard from './components/Card/UpdateParentInfoCard'
import UpdateChildrenInfoCard from './components/Card/UpdateChildrenInfoCard'
import SpeedDialMenuProvider from './components/Context/SpeedDialMenuContext'

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent-success': {
    backgroundColor: '#EBF5F0',
    color: '#57ab82',
    whiteSpace: 'pre-wrap',
    width: '',
    fontFamily: 'sweet-sans-pro, sans-serif',
  },
  '&.notistack-MuiContent-error': {
    width: '',
    backgroundColor: '#f9eeec',
    color: '#C14F3C',
    whiteSpace: 'pre-wrap',
    fontFamily: 'sweet-sans-pro, sans-serif',
  },
}))

// FIXME: Styles for variants no longer exist https://notistack.com/api-reference#overriding-styles, but we may want to keep these colors and backgrounds
// const useStyles = makeStyles((theme) => ({
//   success: {
//     backgroundColor: '#EBF5F0',
//     color: '#57ab82',
//     whiteSpace: 'pre-wrap',
//     width: '',
//     [theme.breakpoints.up('sm')]: {
//       width: '600px',
//     },
//   },

//   error: {
//     width: '',
//     [theme.breakpoints.up('sm')]: {
//       width: '600px',
//     },
//     backgroundColor: '#f9eeec',
//     color: '#C14F3C',
//     whiteSpace: 'pre-wrap',
//   },
// }))

export const fullScreenAssetPath = '/content/asset/fullScreen/:widenAssetKey'

const App: React.FunctionComponent = () => {
  const privateRoutes = [
    {
      /** This initial element is a wrapper for all preceding children */
      element: (
        <>
          {/* 
            TODO: Look into Scroll Restoration 
            https://reactrouter.com/en/main/components/scroll-restoration 
          */}
          <ScrollContextProvider>
            <ScrollToTop />
            <ThemeProvider theme={muiTheme}>
              <CssBaseline />
              <LoadingProvider>
                <NavigationAwayProvider>
                  <PrivateRoute>
                    <UserProvider>
                      <TitleProvider initialTitle="">
                        <AccountProvider>
                          <ProgramDetailsProvider>
                            <SpeedDialMenuProvider>
                              <ContextResetter />
                              <Outlet />
                            </SpeedDialMenuProvider>
                          </ProgramDetailsProvider>
                        </AccountProvider>
                      </TitleProvider>
                    </UserProvider>
                  </PrivateRoute>
                </NavigationAwayProvider>
              </LoadingProvider>
            </ThemeProvider>
          </ScrollContextProvider>
        </>
      ),
      /**
       * !!! ATTENTION DEVELOPERS !!!
       *
       * For the ease of reading, please keep new routes in alphabetical order.
       */
      children: [
        {
          /**
           * The catch-all route to redirect to a known page.
           *
           * TODO: We may want to have a 404 page, or just have a * to catch any other
           * paths not handled and redirect to Account
           */
          path: '/',
          element: (
            <AppScaffold>
              <Outlet />
            </AppScaffold>
          ),
          errorElement: <ErrorElement />,
          children: [
            {
              path: '/account',
              /**
               * Account used to be a tab view which held children nodes.
               * This required use of Outlet within, and likely other parent
               * components will do the same
               */
              element: <Account />,
              errorElement: <ErrorElement />,
              children: [
                {
                  /** Children must begin with their parent path, in this case /account */
                  path: '/account/profile',
                  element: <ProfileTabLayout />,
                  errorElement: <ErrorElement />,
                  /** Loading data before render https://reactrouter.com/en/main/route/loader */
                },
                {
                  /** Children must begin with their parent path, in this case /account */
                  path: '/account/billing',
                  element: <BillingTabLayout />,
                  errorElement: <ErrorElement />,
                },
                {
                  /** Children must begin with their parent path, in this case /account */
                  path: '/account/dashboard',
                  element: <DashboardTab />,
                  errorElement: <ErrorElement />,
                },
                {
                  path: '/account/dashboard/outstanding-invitations',
                  element: <DirectorDashboardDetails />,
                  errorElement: <ErrorElement />,
                },
                {
                  path: '/account/dashboard/outstanding-enrollments',
                  element: <DirectorDashboardDetails />,
                  errorElement: <ErrorElement />,
                },
                {
                  path: '/account/dashboard/programs',
                  element: <DirectorDashboardDetails />,
                  errorElement: <ErrorElement />,
                },
                {
                  /** Children must begin with their parent path, in this case /account */
                  path: '/account/settings',
                  element: <AccountSettingsTabLayout />,
                  errorElement: <ErrorElement />,
                },
                {
                  /** Children must begin with their parent path, in this case /account */
                  element: <InvitesLayout />,
                  errorElement: <ErrorElement />,
                  children: [
                    {
                      path: '/account/invites/*',
                      element: <PendingInvitationsCard />,
                    },
                    {
                      path: '/account/invites/invitation-summary',
                      element: <InvitationSummaryCard />,
                    },
                    {
                      path: '/account/invites/assign-children',
                      element: <AssignChildrenCard />,
                    },
                    {
                      path: '/account/invites/enrollment-summary',
                      element: <EnrollmentCardLayout />,
                    },
                    {
                      path: '/account/invites/add-children',
                      element: <AddChildrenCard />,
                    },
                    {
                      path: '/account/invites/update-parent-info',
                      element: <UpdateParentInfoCard />,
                    },
                    {
                      path: '/account/invites/assign-children/update-children-info',
                      element: <UpdateChildrenInfoCard />,
                    },
                  ],
                },
                {
                  /**
                   * The catch-all route to redirect to /account/profile.
                   *
                   * We can't use defaultAccountPath from AuthProvider because
                   * this isn't contained in the AuthProvider.
                   */
                  path: '/account/*',
                  element: (
                    <Navigate
                      to={{
                        pathname: '/account/profile',
                      }}
                    />
                  ),
                },
              ],
            },
            {
              path: '/invoice/:invoiceId',
              element: <InvoiceDetails />,
              errorElement: <ErrorElement />,
            },
            {
              path: '/admin',
              children: [
                {
                  /** Children must begin with their parent path, in this case /admin */
                  path: '/admin/user-accounts',
                  element: <UserAccounts />,
                  errorElement: <ErrorElement />,
                },
                {
                  /** Children must begin with their parent path, in this case /admin */
                  path: '/admin/roles',
                  element: <UserAccounts />,
                  errorElement: <ErrorElement />,
                },
                {
                  /** Children must begin with their parent path, in this case /admin */
                  path: '/admin/user-accounts/:userId',
                  element: <UserAccounts />,
                  errorElement: <ErrorElement />,
                },
                {
                  /** Children must begin with their parent path, in this case /admin */
                  path: '/admin/regions',
                  element: <Regions />,
                  errorElement: <ErrorElement />,
                  children: [
                    {
                      /** Children must begin with their parent path, in this case /admin/regions */
                      path: '/admin/regions/add-region',
                      element: <AddRegion />,
                      errorElement: <ErrorElement />,
                    },
                    {
                      /** Children must begin with their parent path, in this case /admin/regions */
                      path: '/admin/regions/region-details/:regionKey',
                      element: <RegionDetails />,
                      errorElement: <ErrorElement />,
                    },
                  ],
                },
                {
                  /** Children must begin with their parent path, in this case /admin */
                  path: '/admin/roles',
                  children: [
                    {
                      /** Children must begin with their parent path, in this case /admin/roles */
                      path: '/admin/roles/add-role',
                      element: <AddRole />,
                      errorElement: <ErrorElement />,
                    },
                    {
                      /** Children must begin with their parent path, in this case /admin/roles */
                      path: '/admin/roles/role-details/:roleKey',
                      element: <RoleDetails />,
                      errorElement: <ErrorElement />,
                    },
                  ],
                },
                {
                  /**
                   * The catch-all route to redirect to /admin/user-accounts.
                   */
                  path: '/admin/*',
                  element: (
                    <Navigate
                      to={{
                        pathname: '/admin/user-accounts',
                      }}
                      state={{ from: location }}
                    />
                  ),
                  errorElement: <ErrorElement />,
                },
              ],
            },
            {
              path: '/business',
              element: <Business />,
              errorElement: <ErrorElement />,
              children: [
                {
                  /** Children must begin with their parent path, in this case /business */
                  path: '/business/nda/:assetKey',
                  element: <ContentNDA />,
                  errorElement: <ErrorElement />,
                },
                {
                  /** Children must begin with their parent path, in this case /business */
                  path: '/business/business-content/:assetKey',
                  element: <BusinessContent />,
                  errorElement: <ErrorElement />,
                },
                {
                  /** Children must begin with their parent path, in this case /business */
                  path: '/business/learning-path/:assetKey',
                  element: <LearningPath />,
                  errorElement: <ErrorElement />,
                },
                {
                  /**
                   * The catch-all route to redirect to /business.
                   */
                  path: '/business/*',
                  element: (
                    <Navigate
                      to={{
                        pathname: '/business',
                      }}
                      state={{ from: location }}
                    />
                  ),
                  errorElement: <ErrorElement />,
                },
              ],
            },
            {
              path: '/communities',
              element: <ParentIndex />,
              errorElement: <ErrorElement />,
              children: [
                {
                  index: true,
                  element: <Communities />,
                },
                {
                  /** Children must begin with their parent path, in this case /communities */
                  path: '/communities/add-community',
                  element: <AddCommunity />,
                  errorElement: <ErrorElement />,
                },
                {
                  /** Children must begin with their parent path, in this case /communities */
                  path: '/communities/community-details/:communityKey',
                  element: <CommunityDetails />,
                  errorElement: <ErrorElement />,
                  /** Programs is included within community details, since the program is contained within a community */
                  children: [
                    {
                      /** Children must begin with their parent path, in this case /communities/community-details/:communityKey */
                      path: '/communities/community-details/:communityKey/programs/program-details/:programKey',
                      element: <ProgramDetails />,
                      errorElement: <ErrorElement />,
                    },
                    {
                      /** Children must begin with their parent path, in this case /communities/community-details/:communityKey */
                      path: '/communities/community-details/:communityKey/programs/add-program',
                      element: <AddProgram />,
                      errorElement: <ErrorElement />,
                    },
                    {
                      /** Children must begin with their parent path, in this case /communities/community-details/:communityKey */
                      path: '/communities/community-details/:communityKey/overview',
                      element: <CommunityDetailsLayout />,
                      errorElement: <ErrorElement />,
                    },
                    {
                      /** Children must begin with their parent path, in this case /communities/community-details/:communityKey */
                      path: '/communities/community-details/:communityKey/programs',
                      element: <CommunityProgramLayout />,
                      errorElement: <ErrorElement />,
                    },
                    {
                      /** Children must begin with their parent path, in this case /communities/community-details/:communityKey */
                      path: '/communities/community-details/:communityKey/families',
                      element: <CommunityFamilyLayout />,
                      errorElement: <ErrorElement />,
                    },
                  ],
                },
                {
                  /**
                   * The catch-all route to redirect to /communities.
                   */
                  path: '/communities/*',
                  element: (
                    <Navigate
                      to={{
                        pathname: '/communities',
                      }}
                      state={{ from: location }}
                    />
                  ),
                  errorElement: <ErrorElement />,
                },
              ],
            },
            {
              path: '/events',
              element: <ParentIndex />,
              errorElement: <ErrorElement />,
              children: [
                {
                  index: true,
                  element: <Events />,
                  errorElement: <ErrorElement />,
                },
                {
                  /** Children must begin with their parent path, in this case /events */
                  path: '/events/add-event',
                  element: <AddEvent />,
                  errorElement: <ErrorElement />,
                },
                {
                  /** Children must begin with their parent path, in this case /events */
                  path: '/events/event-details/:eventKey',
                  element: <EventDetails />,
                  errorElement: <ErrorElement />,
                },
              ],
            },
            {
              /**
               * FIXME: This is our opportunity to reroute families under communities,
               * since that's where it resides.
               */
              path: '/families',
              element: <EmptyFamilies />,
              errorElement: <ErrorElement />,
            },
            {
              path: '/family-profile/:userId',
              element: <FamilyProfile />,
              errorElement: <ErrorElement />,
              children: [
                {
                  /** Children must begin with their parent path, in this case /family-profile/:userId */
                  path: '/family-profile/:userId/invoice/:invoiceId?',
                  element: <InvoiceDetails />,
                  errorElement: <ErrorElement />,
                },
              ],
            },
            {
              /**
               * FIXME: This is our opportunity to reroute families under communities,
               * since that's where it resides.
               */
              path: '/invite-new-family',
              element: <InviteFamily />,
              errorElement: <ErrorElement />,
            },
            {
              /**
               * FIXME: This is our opportunity to reroute families under communities,
               * since that's where it resides.
               */
              path: '/invite-existing-family/:userId',
              element: <InviteFamily />,
              errorElement: <ErrorElement />,
            },
            {
              path: '/learning',
              element: <LearningCenter />,
              errorElement: <ErrorElement />,
              children: [
                {
                  /** Children must begin with their parent path, in this case /learning */
                  path: '/learning/:selectedTabKey',
                  element: <LearningCenter />,
                  errorElement: <ErrorElement />,
                },
                {
                  /** Children must begin with their parent path, in this case /learning */
                  path: '/learning/learning-content/:assetKey',
                  element: <LearningCenterContent />,
                  errorElement: <ErrorElement />,
                },
                {
                  /**
                   * The catch-all route to redirect to a known page.
                   */
                  path: '/learning/*',
                  element: (
                    <Navigate
                      to={{
                        pathname: `/learning/${LearningCenterTabs.WelcomeCenter.toLowerCase()}`,
                      }}
                      state={{ from: location }}
                    />
                  ),
                },
              ],
            },
            {
              path: '/team',
              element: <TeamTab />,
              errorElement: <ErrorElement />,
              children: [
                {
                  /** Children must begin with their parent path, in this case /team */
                  path: '/team/add-team-member',
                  element: <AddTeamMember />,
                  errorElement: <ErrorElement />,
                },
                {
                  /** Children must begin with their parent path, in this case /team */
                  path: '/team/invites',
                  element: <TeamTab />,
                  errorElement: <ErrorElement />,
                },
                {
                  /**
                   * The catch-all route to redirect to /team.
                   */
                  path: '/team/*',
                  element: (
                    <Navigate
                      to={{
                        pathname: '/team',
                      }}
                      state={{ from: location }}
                    />
                  ),
                },
              ],
            },
            {
              path: '/termsOfUse',
              element: <TermsOfUse />,
              errorElement: <ErrorElement />,
            },
            /**
             * A test route to enable of the InvitationSummaryCard.
             * (I just don't want to wait to visualize)
             *
             * FIXME: Remove when placing in the flow.
             */
            {
              path: '/test-route',
              element: <InvitationSummaryCard />,
              errorElement: <ErrorElement />,
            },
            {
              /**
               * The catch-all route to redirect to /account/profile.
               */
              path: '/',
              element: (
                <Navigate
                  to={{
                    pathname: '/account/profile',
                  }}
                  state={{ from: location }}
                />
              ),
            },
          ],
        },
        {
          /**
           * We have placed this route outside of "/"
           * as we don't wish to use the AppScaffold nor learning center Layout for this specific route
           */
          path: fullScreenAssetPath,
          element: <FullScreenContainer />,
          errorElement: <ErrorElement />,
        },
      ],
    },
  ]

  const router = createBrowserRouter([
    {
      element: (
        <ThemeProvider theme={muiTheme}>
          <CssBaseline />
          <LoadingProvider>
            <InviteProvider>
              <CountryProvider>
                <Outlet />
                <CookieBanner />
              </CountryProvider>
            </InviteProvider>
          </LoadingProvider>
        </ThemeProvider>
      ),
      children: [
        {
          path: '/country-information',
          element: <CountryInformation />,
          errorElement: <ErrorElement />,
        },
        {
          path: '/invite/:inviteKey',
          element: <InviteForm />,
          errorElement: <ErrorElement />,
        },
        {
          path: '/login',
          element: <Login />,
          errorElement: <ErrorElement />,
          children: [
            {
              path: '/login/reset',
              element: <ResetPassword />,
              errorElement: <ErrorElement />,
            },
          ],
        },
        {
          /**
           * The catch-all route to redirect to /login.
           */
          path: '*',
          element: (
            <Navigate
              to={{
                pathname: '/login',
              }}
              state={{ from: location }}
            />
          ),
        },
        ...privateRoutes,
      ],
    },
  ])

  return (
    /**
     * The Learning Center Context is outside of the Auth Context so we can reset
     * the values on signout and updateActingAs. Since we're not calling anything
     * within the context requiring permissions, this is OK.
     */
    <LearningCenterProvider>
      <BusinessProvider>
        <CommunitiesProvider>
          <AuthProvider>
            <NotiSnackbarProvider
              Components={{
                success: StyledMaterialDesignContent,
                error: StyledMaterialDesignContent,
              }}
              maxSnack={50}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <NotistackSnackbarKeyProvider router={router}>
                <SnackbarProvider router={router}>
                  <RouterProvider router={router} />
                </SnackbarProvider>
              </NotistackSnackbarKeyProvider>
            </NotiSnackbarProvider>
          </AuthProvider>
        </CommunitiesProvider>
      </BusinessProvider>
    </LearningCenterProvider>
  )
}

export const useSidebarLogic = (): {
  openSidebar: boolean
  toggleOpenSidebar: () => void
  shiftContent: boolean
} => {
  const { isLoggedIn } = useAuth()
  const theme = useTheme()
  const isDesktopOrLarger = useMediaQuery(theme.breakpoints.up('lg'))

  const [wasOpenedDueToWidth, setWasOpenedDueToWidth] =
    useState(isDesktopOrLarger)

  // Closed to start unless both logged-in and on desktop.
  const [open, setOpen] = useState(isLoggedIn && isDesktopOrLarger)

  /* adding useEffect to detect changes before the screen is updated
   * and whenever isDesktopOrLarger changes.
   */
  useEffect(() => {
    // Becomes open if the screen widens to desktop widths.
    if (isLoggedIn && isDesktopOrLarger) {
      if (!wasOpenedDueToWidth) {
        setWasOpenedDueToWidth(true)
      }

      if (!open) {
        setOpen(true)
      }
    }
    // Resets to closed on narrowing below lg breakpoint, but obviously doesn't stay closed forever!
    if (isLoggedIn && wasOpenedDueToWidth && !isDesktopOrLarger) {
      setWasOpenedDueToWidth(false)
      setOpen(false)
    }

    // Resets to closed on logging out.
    if (!isLoggedIn && open) {
      setOpen(false)
    }
  }, [isDesktopOrLarger, isLoggedIn, open, wasOpenedDueToWidth])

  const toggleOpenSidebar = React.useCallback(() => {
    setOpen((it) => !it)
  }, [setOpen])

  const shiftContent = isDesktopOrLarger
  const openSidebar = open
  return { openSidebar, toggleOpenSidebar, shiftContent }
}

export default App
